/**
 * The third renderer, and the one that always works.
 *
 * Every failure of the native path ends here: no shell, no printer configured,
 * a printer that is off, a queue that refused, a webview with no canvas. It is
 * `window.print()` on a 72mm-wide page, which is what a browser till already
 * does and what a developer with no thermal printer sees.
 */
import { encodeQr } from './qr'
import { RECEIPT_FONT } from './raster'
import type { Slip, SlipImage } from './slip-model'

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)

export interface HtmlOptions {
  /** Paper width in millimetres. 80mm roll prints 72mm. */
  readonly widthMm?: number
  readonly fontFamily?: string
  /**
   * `@font-face` rules for the receipt's face, pasted into the document.
   *
   * The page is handed to `print()` through a `srcdoc` iframe, which shares
   * nothing with the page that made it but its base URL — a face the kiosk has
   * loaded is not loaded in here. The caller supplies the rules because only it
   * knows where its bundler put the files; without them the slip prints in the
   * fallback face, which is a receipt all the same.
   */
  readonly fontCss?: string
}

/**
 * The QR as inline SVG.
 *
 * Inline because this page is handed to `print()` through an iframe with no
 * network and no origin worth having: an `<img>` at a URL would print as a
 * broken box, and a canvas would have to run before the dialog opens. An SVG
 * of rectangles is in the document by the time the browser lays it out.
 */
function qrSvg(data: string, caption?: string, size = '32mm'): string {
  const { size: count, modules } = encodeQr(data)
  const quiet = 4
  const span = count + quiet * 2
  let path = ''
  for (let row = 0; row < count; row++) {
    for (let column = 0; column < count; column++) {
      if (modules[row * count + column] === true) path += `M${column + quiet} ${row + quiet}h1v1h-1z`
    }
  }
  const label = caption === undefined ? '' : `<div class="qr">${escapeHtml(caption)}</div>`
  return `<div class="qrbox"><svg viewBox="0 0 ${span} ${span}" width="${size}" height="${size}" shape-rendering="crispEdges" role="img" aria-label="e-Barimt QR"><rect width="${span}" height="${span}" fill="#fff"/><path d="${path}" fill="#000"/></svg></div>${label}`
}

/**
 * A logo's width on paper. 8 dots to the millimetre, and never wider than the
 * page — `raster.ts` refuses to upscale for the same reason.
 */
const mm = (dots: number): string => (Math.min(dots, 576) / 8).toFixed(1)

/** An image at its printed width, which is its dot width at 8 dots a millimetre. */
const img = (image: SlipImage): string =>
  `<img src="${escapeHtml(image.data)}" alt="" style="width:${mm(image.dots)}mm">`

export function renderHtml(slip: Slip, options: HtmlOptions = {}): string {
  const width = options.widthMm ?? 72
  const family = options.fontFamily ?? RECEIPT_FONT
  // The raster's body size, in millimetres: 6pt on the 72mm roll, and never
  // below the 16-dot floor `baseSizeFor` holds a narrow head to.
  const base = Math.max(2, width / 32)

  const body = slip.blocks
    .map((block) => {
      switch (block.kind) {
        case 'text':
          return `<div class="t ${block.align ?? 'left'}${block.bold === true ? ' b' : ''}${
            block.size === 'large' ? ' lg' : block.size === 'small' ? ' sm' : ''
          }">${escapeHtml(block.text)}</div>`
        case 'row':
          return `<div class="r${block.bold === true ? ' b' : ''}" style="padding-left:${(block.indent ?? 0) * 4}px"><span>${escapeHtml(
            block.left
          )}</span><span>${escapeHtml(block.right)}</span></div>`
        case 'columns':
          return `<div class="c${block.head === true ? ' h' : ''}">${block.cells
            .map((cell) => `<span>${escapeHtml(cell)}</span>`)
            .join('')}</div>`
        case 'rule':
          return '<hr>'
        case 'space':
          return `<div style="height:calc(${String(block.lines ?? 1)} * var(--base))"></div>`
        case 'number':
          return `<div class="num">${escapeHtml(block.label === undefined ? block.value : `${block.label}: ${block.value}`)}</div>`
        case 'qr':
          return qrSvg(block.data, block.caption)
        case 'barcode':
          return `<div class="t center">${escapeHtml(block.data)}</div>`
        case 'header':
          return `<div class="head${block.image === null ? ' bare' : ''}">${block.image === null ? '' : img(block.image)}${
            block.lines.length === 0
              ? ''
              : `<div class="lines">${block.lines.map((line) => `<div>${escapeHtml(line)}</div>`).join('')}</div>`
          }</div>`
        case 'fiscalPanel':
          return `<div class="fp${block.qr === null ? ' bare' : ''}">${
            block.qr === null ? '' : qrSvg(block.qr, undefined, `${(width * 0.45).toFixed(1)}mm`)
          }<div class="items">${block.items
            .map(
              (item) => `<div class="k">${escapeHtml(item.label)}</div><div class="v">${escapeHtml(item.value)}</div>`
            )
            .join('')}</div></div>`
        default: {
          // The union is closed, so this is unreachable — and the `never`
          // binding is what makes adding a block kind a compile error here
          // rather than a receipt that silently prints the word "undefined"
          // where a line should be.
          const unhandled: never = block
          void unhandled
          return ''
        }
      }
    })
    .join('\n')

  return `<!doctype html>
<meta charset="utf-8">
<title>receipt</title>
<style>
  ${options.fontCss ?? ''}
  @page { size: ${width}mm auto; margin: 0 }
  /* border-box, because the page box is ${width}mm and the padding has to come
     out of it. Under the default content-box the body was ${width}mm *plus*
     6mm of padding, six millimetres wider than the paper — which does not
     error, it just prints every right-hand amount off the edge of the slip. */
  :root { --base: ${base.toFixed(2)}mm }
  body { box-sizing: border-box; width: ${width}mm; margin: 0; padding: 4mm 3mm; font: 500 var(--base)/1.5 ${family}; color: #000; background: #fff }
  .t { white-space: pre-wrap; word-break: break-word; margin-bottom: calc(var(--base) * 0.25) }
  .center { text-align: center } .right { text-align: right }
  .b { font-weight: 800 }
  .lg { font-size: calc(var(--base) * 1.6) }
  .sm { font-size: calc(var(--base) * 0.75) }
  .r { display: flex; justify-content: space-between; gap: 2mm; line-height: 1.75 }
  .r span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
  .r span:last-child { white-space: nowrap }
  /* The design's columns: quantity centred at two thirds, the unit price
     ending at 85%, the amount at the margin — the same stops raster.ts uses. */
  .c { display: grid; grid-template-columns: 58% 16% 11% 15%; line-height: 1.75 }
  .c span:first-child { line-height: 1.3; padding: calc(var(--base) * 0.22) 0; word-break: break-word }
  .c span:nth-child(2) { text-align: center }
  .c span:nth-child(3), .c span:nth-child(4) { text-align: right; white-space: nowrap }
  hr { border: 0; border-top: 0.25mm solid #000; margin: calc(var(--base) * 0.35) 0 }
  .num { text-align: center; font-size: calc(var(--base) * 2); font-weight: 800; line-height: 1.6; white-space: nowrap }
  .qrbox { text-align: center; margin: 4px 0 }
  .qrbox svg { display: block; margin: 0 auto }
  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--base); margin-bottom: calc(var(--base) * 0.5) }
  .head.bare { justify-content: center }
  .head .lines { font-size: calc(var(--base) * 0.75); font-weight: 800; line-height: 1.35; text-align: right; white-space: nowrap }
  .head.bare .lines { text-align: center }
  /* image-rendering is pixelated because the art arrives already one bit: a
     browser that smooths it prints grey mush where the console showed dots,
     and the preview would then be lying about the till. */
  .head img { min-width: 0; max-width: 100%; flex: 0 1 auto; image-rendering: pixelated }
  .fp { display: flex; align-items: center; gap: var(--base); margin-bottom: calc(var(--base) * 0.5) }
  .fp .qrbox { margin: 0 }
  .fp .items { min-width: 0 }
  .fp .k { font-weight: 800; line-height: 1.35 }
  .fp .v { font-weight: 800; font-size: calc(var(--base) * 1.9); line-height: 1.3; margin-bottom: var(--base); white-space: nowrap }
  .fp .v:last-child { margin-bottom: 0 }
  .qr { text-align: center; font-size: 9px; word-break: break-all; margin: 0 0 4px }
</style>
${body}
`
}

/**
 * Print through the browser.
 *
 * A hidden iframe rather than a popup: a kiosk window has no chrome to open one
 * from, and a popup blocker is a failure the customer cannot see or dismiss.
 * The frame is removed after the dialog closes, or after a timeout if it never
 * does — a leaked iframe per receipt is a browser that slows through the day.
 */
export function printViaBrowser(html: string, doc: Document = document): Promise<void> {
  return new Promise((resolve) => {
    const frame = doc.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    doc.body.appendChild(frame)

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      frame.remove()
      resolve()
    }

    frame.onload = () => {
      const view = frame.contentWindow
      if (view === null) return finish()
      view.addEventListener('afterprint', finish, { once: true })
      /*
       * Every image decoded before the dialog opens.
       *
       * `onload` on the frame does not wait for a `data:` image inside
       * `srcdoc` to decode, and `print()` renders what is laid out at that
       * instant — so a logo that lost the race printed as blank paper, on the
       * one path nobody is watching. `decode()` is per-image and already
       * resolved for anything complete, so this costs a microtask on a slip
       * with no logo. A failure to decode still prints: a receipt missing its
       * logo beats no receipt.
       */
      void Promise.all(
        [...view.document.images].map((image) => (image.complete ? Promise.resolve() : image.decode().catch(() => {})))
      ).then(() => {
        try {
          view.focus()
          view.print()
        } catch {
          finish()
        }
        setTimeout(finish, 60_000)
      })
    }

    frame.srcdoc = html
  })
}
